// tutorial: qui inizializziamo il plugin vee-validate e aggiungiamo regole di
// validazione custom
import { Validator } from 'vee-validate';
import { itValidation } from '@/i18n/it';
import { bgValidation } from '@/i18n/bg';
import { czValidation } from '@/i18n/cz';
import { deValidation } from '@/i18n/de';
import { enValidation } from '@/i18n/en';
import { esValidation } from '@/i18n/es';
import { frValidation } from '@/i18n/fr';
import { plValidation } from '@/i18n/pl';
import { skValidation } from '@/i18n/sk';
import { ruValidation } from '@/i18n/ru';

function validateCf(value: string) {
    const cf = value.toUpperCase();
    if (cf === '') {
        return true;
    }
    if (!/^[0-9A-Z]{16}$/.test(cf)) {
        return false;
    }
    const map = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 1, 0, 5, 7, 9, 13, 15, 17,
        19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23,
    ];
    let s = 0;
    for (let i = 0; i < 15; i++) {
        let c = cf.charCodeAt(i);
        if (c < 65) {
            c = c - 48;
        } else {
            c = c - 55;
        }
        if (i % 2 === 0) {
            s += map[c];
        } else {
            s += c < 10 ? c : c - 10;
        }
    }
    const expected = String.fromCharCode(65 + s % 26);
    if (expected !== cf.charAt(15)) {
        return false;
    }
    return true;
}

function validatePiva(value: string) {
    const pi = value;
    if (pi === '') {
        return true;
    }
    if (!/^[0-9]{11}$/.test(pi)) {
        return false;
    }
    let s = 0;
    for (let i = 0; i <= 9; i += 2) {
        s += pi.charCodeAt(i) - '0'.charCodeAt(0);
    }
    for (let i = 1; i <= 9; i += 2) {
        let c = 2 * (pi.charCodeAt(i) - '0'.charCodeAt(0));
        if (c > 9) {
            c = c - 9;
        }
        s += c;
    }
    const expected = (10 - s % 10) % 10;
    if (expected !== pi.charCodeAt(10) - '0'.charCodeAt(0)) {
        return false;
    }
    return true;
}

function validatePhone(value: string) {
    return /^(\+)?([-()/. 0-9]){5,20}$/.test(value);
}

function validatePass(value: string) {
    let counter = 0;
    const passwordMinCharacterSets = 4;
    const pass = value;
    if (pass === '') {
        return true;
    }
    if (pass.match(/\d+/)) {
        counter++;
    }

    if (pass.match(/[a-z]+/)) {
        counter++;
    }

    if (pass.match(/[A-Z]+/)) {
        counter++;
    }

    if (pass.match(/[^a-zA-Z0-9]+/)) {
        counter++;
    }

    if (counter < passwordMinCharacterSets) {
        return false;
    } else {
        return true;
    }
}

function luhnCheck(val: string) {
    let checksum = 0;
    let j = 1;
    for (let i = val.length - 1; i >= 0; i--) {
      let calc = 0;
      calc = Number(val.charAt(i)) * j;
      if (calc > 9) {
        checksum = checksum + 1;
        calc = calc - 10;
      }
      checksum = checksum + calc;
      if (j === 1) {
        j = 2;
      } else {
        j = 1;
      }
    }
    return (checksum % 10) === 0;
}

export function validateCardNumber(val: string) {
    const regex = new RegExp('^[0-9]{13,19}$');
    if (!regex.test(val)) {
        return false;
    }
    return luhnCheck(val);
}

export function validateCvc(val: string) {
    const regex = new RegExp('^[0-9]{3,4}$');
    if (!regex.test(val)) {
        return false;
    }
    return true;
}

export default function(i18n: any) {
    Validator.extend('validate-cf', {
        getMessage: (field) => 'Codice fiscale non valido',
        validate: (value) => {
            return validateCf(value);
        },
    });

    Validator.extend('validate-piva', {
        getMessage: (field) => 'Partita IVA non valida.',
        validate: (value) => {
            return validatePiva(value);
        },
    });

    Validator.extend('validate-piva-cf', {
        getMessage: (field) => 'Partita IVA / Codice fiscale non valido.',
        validate: (value) => {
            return validatePiva(value) || validateCf(value);
        },
    });

    Validator.extend('validate-gender', {
        getMessage: (field) => 'Il campo Sesso è richiesto',
        validate: (value) => {
            return value && value > 0;
        },
    });

    Validator.extend('validate-telephone', {
        getMessage: (field) => 'Partita IVA / Codice fiscale non valido.',
        validate: (value) => {
            return validatePhone(value);
        },
    });

    Validator.extend('validate-regionid', {
        getMessage: (field) => 'Il campo Provincia è richiesto',
        validate: (value) => {
            return value && value > 0;
        },
    });

    Validator.extend('validate-ascii', {
        getMessage: (field) => 'Il campo non deve contenere caretteri speciali',
        validate: (value) => {
            return /^[\x00-\x7F]*$/.test(value);
        },
    });

    Validator.extend('validate-streetandnumber', {
        getMessage: (field) => 'Il campo non deve contenere caretteri speciali',
        validate: (value, args: any) => {
            const len = args[1];
            const streetNum = args[0];
            return value.length + streetNum.length <= len;
        },
    }, {hasTarget: true});

    Validator.extend('validate-data-consensus', {
        getMessage: (field) => 'Your consent is required for the processing of personal data.',
        validate: (value, args: any) => {
            return value;
        },
    }, {hasTarget: true});

    Validator.extend('validate-password', {
        getMessage: (field) => 'The password must be made up of at least 8 characters, including a capital letter, a small letter, a number and a special character (.;,!$#).',
        validate: (value) => {
            return validatePass(value);
        },
    });

    return {
        events: '',
        classes: true,
        classNames: {
            valid: 'valid',
            invalid: 'mage-error',
        },
        aria: false,
        i18n,
        dictionary: {
            it: itValidation,
            bg: bgValidation,
            cz: czValidation,
            de: deValidation,
            en: enValidation,
            es: esValidation,
            fr: frValidation,
            pl: plValidation,
            sk: skValidation,
            ru: ruValidation,
        },
    };
}
